import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  AppBar,
  Toolbar,
  Snackbar,
  Tooltip
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import gmailService from './services/gmailService';
import fileStorage from './utils/fileStorage';
import filterXmlExport from './utils/filterXmlExport';
import SenderList from './components/SenderList';
import AdContainer from './components/AdContainer';
import SidebarAd from './components/SidebarAd';

function App() {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [senders, setSenders] = useState([]);
  const [selectedSenders, setSelectedSenders] = useState(new Set());
  const [savedSelections, setSavedSelections] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState('');
  const [activeOperations, setActiveOperations] = useState(new Map()); // Track multiple operations
  const [operationQueue, setOperationQueue] = useState([]); // Queue for delete operations
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    initializeGapi();
    loadFromLocalStorage();
    restoreAppState();
  }, []);

  // Process operation queue
  useEffect(() => {
    const processQueue = async () => {
      if (isProcessingQueue || operationQueue.length === 0) return;

      setIsProcessingQueue(true);
      const operation = operationQueue[0];

      try {
        await operation.execute();
      } catch (err) {
        console.error('Queue operation failed:', err);
      }

      // Remove completed operation from queue
      setOperationQueue(prev => prev.slice(1));
      setIsProcessingQueue(false);
    };

    processQueue();
  }, [operationQueue, isProcessingQueue]);

  const initializeGapi = async () => {
    try {
      await gmailService.init();
      setIsInitialized(true);
      setIsSignedIn(gmailService.isSignedIn());
    } catch (err) {
      if (err.details && err.details.includes('Not a valid origin')) {
        setError('OAuth origin not registered yet. Wait 60 seconds, then hard refresh (Ctrl+Shift+R).');
      } else {
        setError('Failed to initialize Gmail API. Check console for details.');
      }
      console.error('Initialization error:', err);
    }
  };

  const handleSignIn = async () => {
    try {
      setError(null);
      await gmailService.signIn();
      setIsSignedIn(true);
    } catch (err) {
      setError('Failed to sign in. Please try again.');
      console.error(err);
    }
  };

  const handleSignOut = async () => {
    const confirmed = window.confirm('Are you sure you want to sign out? This will clear all your loaded data.');
    if (!confirmed) return;

    try {
      await gmailService.signOut();
      setIsSignedIn(false);
      setSenders([]);
      setSelectedSenders(new Set());
      setSavedSelections(new Set());
      // Clear app state from localStorage
      localStorage.removeItem('gmail_helper_app_state');
      setSuccessMessage('Signed out successfully');
    } catch (err) {
      setError('Failed to sign out.');
      console.error(err);
    }
  };

  const handleLoadSenders = async () => {
    try {
      setError(null);
      setLoading(true);
      setLoadingMessage('Fetching email list...');
      setProgress(0);

      const messageIds = await gmailService.getAllMessageIds();
      setLoadingMessage(`Loading ${messageIds.length} emails...`);

      const sendersData = await gmailService.getMessageHeaders(
        messageIds,
        (current, total) => {
          setProgress((current / total) * 100);
          setLoadingMessage(`Processing emails: ${current} of ${total}`);
        }
      );

      // Filter out senders with 0 emails ONLY if they weren't previously selected
      const filteredSenders = sendersData.filter(sender =>
        sender.count > 0 || selectedSenders.has(sender.email)
      );

      setSenders(filteredSenders);

      // On first load (no senders yet), select all by default
      // On rescan, preserve existing selections
      if (senders.length === 0) {
        // First load - select all
        setSelectedSenders(new Set(filteredSenders.map(s => s.email)));
      } else {
        // Rescan - preserve selections, don't auto-add new senders
        const newSelections = new Set(selectedSenders);
        // Remove selections for senders that no longer exist
        Array.from(newSelections).forEach(email => {
          if (!filteredSenders.find(s => s.email === email)) {
            newSelections.delete(email);
          }
        });
        setSelectedSenders(newSelections);
      }
      setLoadingMessage('');
      setProgress(0);
    } catch (err) {
      console.error('Load senders error:', err);

      // Handle different error types with user-friendly messages
      if (err.result && err.result.error) {
        const errorCode = err.result.error.code;
        const errorStatus = err.result.error.status;

        if (errorCode === 401 || errorStatus === 'UNAUTHENTICATED') {
          setError('Your session has expired. Please sign out and sign in again.');
          // Auto sign out after showing message
          setTimeout(() => {
            handleSignOut();
          }, 3000);
        } else if (errorCode === 403) {
          setError('Permission denied. Gmail API may not be enabled in your Google Cloud Console.');
        } else if (errorCode === 429) {
          setError('Too many requests. Please wait a few minutes and try again.');
        } else {
          setError(`Unable to load emails. Error: ${err.result.error.message || 'Unknown error'}`);
        }
      } else if (err.message && err.message.includes('network')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError('Unable to load emails. Please try signing out and signing in again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSender = (email) => {
    const newSelected = new Set(selectedSenders);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedSenders(newSelected);
  };

  const handleToggleAll = (checked) => {
    if (checked) {
      setSelectedSenders(new Set(senders.map(s => s.email)));
    } else {
      setSelectedSenders(new Set());
    }
  };

  const loadFromLocalStorage = () => {
    const stored = fileStorage.loadFromLocalStorage();
    if (stored) {
      setSavedSelections(stored.selectedSenders);
      setSuccessMessage('Previous selections loaded from browser storage');
    }
  };

  const restoreAppState = () => {
    // Restore sender list and selections from localStorage
    const appState = localStorage.getItem('gmail_helper_app_state');
    if (appState) {
      try {
        const state = JSON.parse(appState);
        if (state.senders && state.senders.length > 0) {
          setSenders(state.senders);
          if (state.selectedSenders) {
            setSelectedSenders(new Set(state.selectedSenders));
          }
          if (state.savedSelections) {
            setSavedSelections(new Set(state.savedSelections));
          }
          console.log('App state restored:', state.senders.length, 'senders');
        }
      } catch (e) {
        console.error('Failed to restore app state:', e);
      }
    }
  };

  const saveAppState = () => {
    // Save sender list and selections to localStorage
    const state = {
      senders,
      selectedSenders: Array.from(selectedSenders),
      savedSelections: Array.from(savedSelections),
      timestamp: Date.now()
    };
    localStorage.setItem('gmail_helper_app_state', JSON.stringify(state));
  };

  // Save app state whenever senders or selections change
  useEffect(() => {
    if (senders.length > 0) {
      const state = {
        senders,
        selectedSenders: Array.from(selectedSenders),
        savedSelections: Array.from(savedSelections),
        timestamp: Date.now()
      };
      localStorage.setItem('gmail_helper_app_state', JSON.stringify(state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senders, selectedSenders, savedSelections]);

  const handleSaveSelections = () => {
    try {
      fileStorage.saveSelections(selectedSenders, senders);
      setSavedSelections(new Set(selectedSenders));
      setSuccessMessage('Selections saved to file and browser storage!');
    } catch (err) {
      setError('Failed to save selections.');
      console.error(err);
    }
  };

  const handleLoadSelectionsFromFile = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      const stored = await fileStorage.loadSelectionsFromFile(file);

      // If we have sender metadata in the file, reconstruct the senders list
      if (stored.senderMetadata && Object.keys(stored.senderMetadata).length > 0) {
        const reconstructedSenders = Object.entries(stored.senderMetadata).map(([email, metadata]) => ({
          email,
          displayName: metadata.displayName,
          count: metadata.count
        }));
        setSenders(reconstructedSenders);
      }

      setSelectedSenders(stored.selectedSenders);
      setSavedSelections(stored.selectedSenders);
      setSuccessMessage(`Selections loaded from ${file.name}. ${stored.selectedSenders.size} senders selected.`);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError('Failed to load selections from file.');
      console.error(err);
    }
  };

  const handleSyncFilters = async () => {
    try {
      setError(null);
      setLoading(true);

      setLoadingMessage('Syncing filters...');
      setProgress(0);

      // On first sync, use all senders as "previously selected" so unselected ones get filtered
      // On subsequent syncs, compare against what was previously synced
      const previousSync = savedSelections.size > 0
        ? savedSelections
        : new Set(senders.map(s => s.email));

      const result = await gmailService.syncFilters(
        previousSync,
        selectedSenders,
        (current, total, action) => {
          setProgress((current / total) * 100);
          const actionText = action === 'removing' ? 'Removing filters' : 'Creating filters';
          setLoadingMessage(`${actionText}: ${current} of ${total}`);
        }
      );

      setSavedSelections(new Set(selectedSenders));
      fileStorage.saveSelections(selectedSenders, senders);

      setLoadingMessage('');
      setProgress(0);
      setSuccessMessage(
        `Sync complete! Removed ${result.removed} filters, created ${result.created} filters.`
      );
    } catch (err) {
      setError('Failed to sync filters. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportFiltersXml = () => {
    try {
      const nonSelectedSenders = senders
        .filter(s => !selectedSenders.has(s.email))
        .map(s => s.email);

      if (nonSelectedSenders.length === 0) {
        setError('No senders to filter. All senders are selected.');
        return;
      }

      // Get user email from Google API if available
      const userEmail = gmailService.gapi?.client?.getToken()?.access_token
        ? 'user@gmail.com'
        : 'user@gmail.com';

      filterXmlExport.downloadXml(nonSelectedSenders, userEmail, 'Gmail User');
      setSuccessMessage(`Exported ${nonSelectedSenders.length} filters to gmail-filters.xml. Import in Gmail Settings > Filters and Blocked Addresses.`);
    } catch (err) {
      setError('Failed to export filters XML.');
      console.error(err);
    }
  };

  const handleExportInverseFiltersXml = () => {
    try {
      const selectedSenderEmails = senders
        .filter(s => selectedSenders.has(s.email))
        .map(s => s.email);

      if (selectedSenderEmails.length === 0) {
        setError('No senders selected. Please select senders to keep in inbox.');
        return;
      }

      // Get user email from Google API if available
      const userEmail = gmailService.gapi?.client?.getToken()?.access_token
        ? 'user@gmail.com'
        : 'user@gmail.com';

      filterXmlExport.downloadInverseXml(selectedSenderEmails, userEmail, 'Gmail User');
      setSuccessMessage(`Exported inverse filter for ${selectedSenderEmails.length} selected senders to gmail-filters-inverse.xml. This will keep selected emails in inbox and archive everything else.`);
    } catch (err) {
      setError('Failed to export inverse filters XML.');
      console.error(err);
    }
  };

  const handleDeleteAllFilters = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete ALL Gmail filters? This cannot be undone!'
    );

    if (!confirmed) return;

    try {
      setError(null);
      setLoading(true);
      setLoadingMessage('Fetching all filters...');
      setProgress(0);

      const result = await gmailService.deleteAllFilters(
        (current, total) => {
          setProgress((current / total) * 100);
          setLoadingMessage(`Deleting filters: ${current} of ${total}`);
        }
      );

      setLoadingMessage('');
      setProgress(0);
      setSuccessMessage(`Successfully deleted ${result.deleted} of ${result.total} filters!`);
    } catch (err) {
      setError('Failed to delete filters. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllFrom = async (senderEmail, displayName) => {
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE all emails from "${displayName}" (${senderEmail})?\n\nThis action cannot be undone!`
    );

    if (!confirmed) return;

    const operationId = `delete-${senderEmail}-${Date.now()}`;

    // Add to queue with status "queued"
    setActiveOperations(prev => new Map(prev).set(operationId, {
      message: `Queued: ${displayName}`,
      progress: 0,
      status: 'queued'
    }));

    // Create operation to add to queue
    const operation = {
      id: operationId,
      execute: async () => {
        try {
          setError(null);

          // Update status to "processing"
          setActiveOperations(prev => new Map(prev).set(operationId, {
            message: `Finding emails from ${displayName}...`,
            progress: 0,
            status: 'processing'
          }));

          const result = await gmailService.deleteAllEmailsFromSender(
            senderEmail,
            (current, total) => {
              setActiveOperations(prev => new Map(prev).set(operationId, {
                message: `Deleting ${displayName}: ${current} of ${total}`,
                progress: (current / total) * 100,
                status: 'processing'
              }));
            }
          );

          // Remove operation from map
          setActiveOperations(prev => {
            const newMap = new Map(prev);
            newMap.delete(operationId);
            return newMap;
          });

          if (result.total === 0) {
            setSuccessMessage(`No emails found from ${senderEmail}`);
          } else {
            setSuccessMessage(`Successfully deleted ${result.deleted} of ${result.total} emails from ${senderEmail}!`);
          }
        } catch (err) {
          // Remove operation from map on error
          setActiveOperations(prev => {
            const newMap = new Map(prev);
            newMap.delete(operationId);
            return newMap;
          });
          setError(`Failed to delete emails from ${senderEmail}. Please try again.`);
          console.error(err);
        }
      }
    };

    // Add to queue
    setOperationQueue(prev => [...prev, operation]);
  };

  const hasUnsavedChanges = () => {
    // If no saved state yet, consider any selection changes as unsaved
    if (savedSelections.size === 0 && selectedSenders.size > 0) return true;
    if (selectedSenders.size !== savedSelections.size) return true;
    for (const email of selectedSenders) {
      if (!savedSelections.has(email)) return true;
    }
    return false;
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: '#fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <AppBar position="static">
          <Toolbar>
            <EmailIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Gmail Helper
            </Typography>
            {isSignedIn && (
              <Tooltip title="Sign out and clear all data">
                <Button
                  color="inherit"
                  onClick={handleSignOut}
                  startIcon={<LogoutIcon />}
                >
                  Sign Out
                </Button>
              </Tooltip>
            )}
          </Toolbar>
        </AppBar>

        {/* Top Banner Ad (728x90) */}
        <AdContainer
          id="ad-header"
          height="90px"
          slot="6513679635"
          format="auto"
          responsive={true}
        />

        {/* Action Buttons - Only show when signed in and have senders */}
        {isSignedIn && senders.length > 0 && (
          <Container maxWidth="lg">
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6">
                Email Senders ({senders.length} unique)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleLoadSelectionsFromFile}
                />
                <Tooltip title="Loads a saved sender list with selections">
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    size="small"
                  >
                    Load
                  </Button>
                </Tooltip>
                <Tooltip title="Saves a local file with sender list with selections">
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSelections}
                    disabled={loading || senders.length === 0}
                    size="small"
                  >
                    Save
                  </Button>
                </Tooltip>
                <Tooltip title="Rescans your emails and creates new sender list">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EmailIcon />}
                    onClick={handleLoadSenders}
                    disabled={loading}
                    size="small"
                  >
                    Rescan
                  </Button>
                </Tooltip>
                <Tooltip title="Exports a file that Filters and Blocked Addresses can import to set up filtering for email addresses that you don't want in your inbox">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportFiltersXml}
                    disabled={loading || selectedSenders.size === senders.length}
                    size="small"
                  >
                    Export XML
                  </Button>
                </Tooltip>
                <Tooltip title="Export inverse filter: keeps SELECTED senders in inbox, archives everything else">
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportInverseFiltersXml}
                    disabled={loading || selectedSenders.size === 0}
                    size="small"
                  >
                    Export Inverse
                  </Button>
                </Tooltip>
                <Tooltip title="Remove all filters in Gmail settings for Filters and Blocked Addresses">
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteAllFilters}
                    disabled={loading}
                    size="small"
                  >
                    Delete All Filters
                  </Button>
                </Tooltip>
                {hasUnsavedChanges() && (
                  <Tooltip title="Synchronize filter changes with Gmail based on your selection modifications">
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<SyncIcon />}
                      onClick={handleSyncFilters}
                      disabled={loading}
                      size="small"
                    >
                      Sync Changes
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Warning Alert */}
            {hasUnsavedChanges() && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                You have unsaved changes. Click "Sync Changes" to update filters, or "Save" to save selections.
              </Alert>
            )}
          </Container>
        )}

        {/* Loading Progress - Always visible when loading */}
        {loading && (
          <Container maxWidth={false} sx={{ p: 0, m: 0, width: '100%' }}>
            <Box sx={{ mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mx: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                {loadingMessage}
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          </Container>
        )}

        {/* Active Operations Progress - Show all concurrent operations */}
        {activeOperations.size > 0 && (
          <Container maxWidth={false} sx={{ p: 0, m: 0, width: '100%' }}>
            {Array.from(activeOperations.entries()).map(([id, operation]) => (
              <Box key={id} sx={{ mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mx: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                  {operation.message}
                </Typography>
                <LinearProgress variant="determinate" value={operation.progress} />
              </Box>
            ))}
          </Container>
        )}
      </Box>

      <Container maxWidth={false} sx={{ mt: 4, mb: 4, flex: 1, p: 0, m: 0, width: '100%' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!isInitialized && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Initializing Gmail API...</Typography>
          </Paper>
        )}

        {isInitialized && !isSignedIn && (
          <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
              Gmail Helper - Email Sender Management Tool
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              What is Gmail Helper?
            </Typography>
            <Typography variant="body1" paragraph>
              Gmail Helper is a web application that helps you manage and filter your Gmail inbox by analyzing email senders.
              Take control of your inbox by easily identifying which senders to keep and which to filter out.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              The Story Behind Gmail Helper:
            </Typography>
            <Typography variant="body1" paragraph>
              In 2012, I switched away from Gmail being my primary email account and began using Gmail as the address I'd use when
              signing up for non-serious interests. It turned Gmail into a swamp, which I occasionally perused. Then OAuth2 happened
              and everything that allowed "Sign in with Google" started using Gmail as the point of communications. I had to find a way
              to clean my mailbox up so that I could see the emails from my critical providers. I used this tool to go from 37,000+
              unread emails to just the critical emails in my inbox without trying to train an AI to know what's important and what isn't.
              <strong>You decide what matters</strong> - not an algorithm.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Key Features:
            </Typography>
            <Box component="ul" sx={{ mb: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Scan Your Inbox:</strong> Automatically analyze all emails to identify unique senders and email counts
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Bulk Selection Management:</strong> Select or deselect senders you want to keep in your inbox
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Filter Export:</strong> Generate Gmail filter XML files to automatically archive unwanted emails
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Inverse Filtering:</strong> Create a single catch-all filter that keeps selected senders in inbox while archiving everything else
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>API Sync:</strong> Synchronize filters directly to Gmail via API without manual import
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Bulk Delete:</strong> Permanently delete all emails from specific senders
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Save & Load:</strong> Save your sender selections to a file and reload them later
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Privacy & Security:
            </Typography>
            <Typography variant="body1" paragraph>
              Gmail Helper requires read and modify access to your Gmail account to scan messages, create filters, and delete emails.
              All operations are performed directly between your browser and Gmail's API. <strong>No data is stored centrally or on our servers.</strong> All
              sender data and selections are stored only in your browser's local storage on your device. You maintain complete control over your data.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              How It Works:
            </Typography>
            <Typography variant="body1" paragraph>
              1. Sign in with your Google account<br />
              2. Scan your Gmail to load all senders<br />
              3. Select which senders you want to keep<br />
              4. Export filters or sync directly to Gmail<br />
              5. Unwanted emails are automatically archived or deleted
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Tips for Best Results:
            </Typography>
            <Box component="ul" sx={{ mb: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Inverse Filter is Recommended:</strong> Creates just one catch-all filter instead of individual rules per sender, making management easier
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Rescan Often When Starting:</strong> Email counts are not updated dynamically after bulk deletes, so rescan to see updated numbers
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Check Then Delete:</strong> You can check (keep) a sender but still delete all their emails - they'll remain in your allowed list for future emails
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <strong>Note on Sync vs Export:</strong> Sync and Export XML create one filter rule per sender, which can be hundreds of rules. Inverse Filter is more efficient.
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button variant="contained" size="large" onClick={handleSignIn}>
                Sign In with Google
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                By signing in, you authorize Gmail Helper to access your Gmail account
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  Privacy Policy
                </a>
                {' | '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  Terms of Service
                </a>
              </Typography>
            </Box>
          </Paper>
        )}

        {isSignedIn && (
          <>
            {senders.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Get started by loading senders or importing a saved file
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={handleLoadSelectionsFromFile}
                  />
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    Load from File
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleLoadSenders}
                    disabled={loading}
                  >
                    Scan Gmail
                  </Button>
                </Box>
              </Paper>
            ) : (
              <>
                {/* Content Area - Fixed height with flex layout */}
                <Box sx={{
                  display: 'flex',
                  gap: 0,
                  height: 'calc(100vh - 350px)',
                  minHeight: '500px',
                  overflow: 'hidden'
                }}>
                  {/* Scrollable Table Container */}
                  <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'auto',
                    height: '100%'
                  }}>
                    <SenderList
                      senders={senders}
                      selectedSenders={selectedSenders}
                      onToggleSender={handleToggleSender}
                      onToggleAll={handleToggleAll}
                      onDeleteAllFrom={handleDeleteAllFrom}
                    />
                  </Box>

                  {/* Fixed Sidebar - 200px with centered ad */}
                  <Box sx={{
                    width: 200,
                    flexShrink: 0,
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    paddingTop: '50px',
                    height: '100%',
                    overflow: 'hidden'
                  }}>
                    <SidebarAd
                      id="ad-sidebar"
                      width="180px"
                      height="250px"
                      slot="9303494508"
                    />
                  </Box>
                </Box>
              </>
            )}
          </>
        )}

        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
        />
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #e0e0e0',
          textAlign: 'center'
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Button
            href="https://www.jpmorgenthal.com/gmailhelper/privacy"
            size="small"
            sx={{ mx: 1 }}
          >
            Privacy Policy
          </Button>
          <Button
            href="https://www.jpmorgenthal.com/gmailhelper/terms"
            size="small"
            sx={{ mx: 1 }}
          >
            Terms of Service
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          © 2025 Platform Masters, LLC. All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default App;
